import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCashRegister, faDesktop, faUtensils } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useAuth } from '../../contexts/AuthProvider';
import classes from './backoffice-page.module.scss';

type BackofficePageProps = {
  tenantId: string;
};

type BackofficeOption = {
  key: 'pos' | 'kds' | 'dashboard';
  label: string;
  to: string;
  icon: IconProp;
};

// Summary: Tenant-scoped backoffice home hub with role-based quick links.
// Details: docs/function-library/web/contexts-pages.md#pages-backofficepage
export const BackofficePage: React.FC<BackofficePageProps> = ({ tenantId }) => {
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role ?? 'user';

  const options: BackofficeOption[] = [
    {
      key: 'pos',
      label: 'קופה',
      to: `/t/${tenantId}/pos`,
      icon: faCashRegister as IconProp,
    },
    {
      key: 'kds',
      label: 'מסך מטבח',
      to: `/t/${tenantId}/kds`,
      icon: faUtensils as IconProp,
    },
    ...(role === 'admin' || role === 'super_admin'
      ? [
          {
            key: 'dashboard' as const,
            label: 'דשבורד',
            to: `/t/${tenantId}/dashboard`,
            icon: faDesktop as IconProp,
          },
        ]
      : []),
  ];

  return (
    <section className={classes.container} dir="rtl" aria-labelledby="backoffice-home-title">
      <div className={classes.card}>
        <div className={classes.titleBlock}>
          <h1 id="backoffice-home-title" className={classes.title}>בית</h1>
          <p className={classes.subtitle}>בחר מסך עבודה</p>
        </div>
        <div className={classes.grid}>
          {options.map((option) => (
            <NavLink
              key={option.key}
              to={option.to}
              state={option.key === 'kds' ? { from: location.pathname } : undefined}
              className={classes.tile}
              data-testid={`backoffice-tile-${option.key}`}
            >
              <span className={classes.iconWrap}>
                <FontAwesomeIcon icon={option.icon} className={classes.icon} />
              </span>
              <span className={classes.label}>{option.label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </section>
  );
};
